/**
 * Performance Metrics Service
 * 
 * Calculates and provides user performance metrics and analytics.
 * 
 * @module PerformanceMetricsService
 */

import {
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Performance period types
 */
export type PerformancePeriod = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

/**
 * User performance metrics interface
 */
export interface UserPerformanceMetrics {
  userId: string;
  userName: string;
  userEmail: string;
  period: PerformancePeriod;
  startDate: Date;
  endDate: Date;
  
  // Sales metrics
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  pendingOrders: number;
  completedOrders: number;
  
  // Store metrics
  storesVisited: number;
  storesManaged: number;
  newStoresAdded: number;
  
  // Activity metrics
  loginCount: number;
  totalTimeSpent: number; // in minutes
  averageSessionDuration: number; // in minutes
  lastActivity: Date | null;
  
  // Performance indicators
  orderCompletionRate: number; // percentage
  targetAchievementRate: number; // percentage
  salesTarget?: number;
  ordersTarget?: number;
  
  // Rankings
  orderRank?: number;
  revenueRank?: number;
}

/**
 * Performance comparison data
 */
export interface PerformanceComparison {
  user: UserPerformanceMetrics;
  average: {
    totalOrders: number;
    totalRevenue: number;
    storesVisited: number;
  };
  percentile: number; // User's percentile ranking (0-100)
}

/**
 * Team performance metrics
 */
export interface TeamPerformanceMetrics {
  totalUsers: number;
  activeUsers: number;
  totalOrders: number;
  totalRevenue: number;
  averageOrdersPerUser: number;
  averageRevenuePerUser: number;
  topPerformers: UserPerformanceMetrics[];
  bottomPerformers: UserPerformanceMetrics[];
  periodComparison: {
    orderGrowth: number; // percentage
    revenueGrowth: number; // percentage
  };
}

/**
 * Performance Metrics Service Class
 */
export class PerformanceMetricsService {
  private static readonly ORDERS_COLLECTION = 'StoreOrders';
  private static readonly STORES_COLLECTION = 'Stores';
  private static readonly ACTIVITIES_COLLECTION = 'activities';
  private static readonly USERS_COLLECTION = 'users';

  /**
   * Get user performance metrics
   * 
   * @param userId - User ID
   * @param period - Performance period
   * @param startDate - Optional start date for custom period
   * @param endDate - Optional end date for custom period
   * @returns Promise resolving to user performance metrics
   */
  static async getUserPerformance(
    userId: string,
    period: PerformancePeriod = 'monthly',
    startDate?: Date,
    endDate?: Date
  ): Promise<UserPerformanceMetrics> {
    try {
      const dates = this.getPeriodDates(period, startDate, endDate);
      
      // Get user info
      const userDoc = await getDocs(query(collection(db, this.USERS_COLLECTION), where('__name__', '==', userId)));
      const userData = userDoc.docs[0]?.data();
      
      // Get orders for the user in the period
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      const ordersSnapshot = await getDocs(query(
        ordersQuery,
        where('salesRepId', '==', userId),
        where('orderDate', '>=', Timestamp.fromDate(dates.start)),
        where('orderDate', '<=', Timestamp.fromDate(dates.end))
      ));

      const orders = ordersSnapshot.docs.map(doc => doc.data());
      
      // Calculate metrics
      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'submitted').length;
      const completedOrders = orders.filter(o => o.status === 'completed' || o.status === 'approved').length;
      const orderCompletionRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;

      // Get activity metrics
      const activitiesQuery = query(
        collection(db, this.ACTIVITIES_COLLECTION),
        where('userId', '==', userId),
        where('timestamp', '>=', Timestamp.fromDate(dates.start)),
        where('timestamp', '<=', Timestamp.fromDate(dates.end))
      );
      const activitiesSnapshot = await getDocs(activitiesQuery);
      const activities = activitiesSnapshot.docs.map(doc => doc.data());
      
      const loginCount = activities.filter(a => a.action === 'login').length;
      const lastActivity = activities.length > 0 
        ? activities[0].timestamp?.toDate?.() || null
        : null;

      return {
        userId,
        userName: userData?.displayName || 'Unknown',
        userEmail: userData?.email || '',
        period,
        startDate: dates.start,
        endDate: dates.end,
        totalOrders,
        totalRevenue,
        averageOrderValue,
        pendingOrders,
        completedOrders,
        storesVisited: 0, // Would need store visit tracking
        storesManaged: 0, // Would need to query stores collection
        newStoresAdded: 0,
        loginCount,
        totalTimeSpent: 0,
        averageSessionDuration: 0,
        lastActivity,
        orderCompletionRate,
        targetAchievementRate: 0,
        salesTarget: userData?.salesTarget,
        ordersTarget: userData?.ordersTarget
      };
    } catch (error: any) {
      console.error('Error getting user performance:', error);
      throw new Error(`Failed to get user performance: ${error.message}`);
    }
  }

  /**
   * Get team performance metrics
   * 
   * @param period - Performance period
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Promise resolving to team performance metrics
   */
  static async getTeamPerformance(
    period: PerformancePeriod = 'monthly',
    startDate?: Date,
    endDate?: Date
  ): Promise<TeamPerformanceMetrics> {
    try {
      const dates = this.getPeriodDates(period, startDate, endDate);
      
      // Get all users
      const usersSnapshot = await getDocs(collection(db, this.USERS_COLLECTION));
      const users = usersSnapshot.docs;
      const totalUsers = users.length;
      const activeUsers = users.filter(u => u.data().status === 'active').length;

      // Get all orders in period
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      const ordersSnapshot = await getDocs(query(
        ordersQuery,
        where('orderDate', '>=', Timestamp.fromDate(dates.start)),
        where('orderDate', '<=', Timestamp.fromDate(dates.end))
      ));

      const orders = ordersSnapshot.docs.map(doc => doc.data());
      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

      // Get performance for each user
      const userPerformances: UserPerformanceMetrics[] = [];
      for (const userDoc of users) {
        try {
          const perf = await this.getUserPerformance(userDoc.id, period, dates.start, dates.end);
          userPerformances.push(perf);
        } catch (error) {
          console.warn(`Failed to get performance for user ${userDoc.id}`);
        }
      }

      // Sort by total orders
      userPerformances.sort((a, b) => b.totalOrders - a.totalOrders);

      return {
        totalUsers,
        activeUsers,
        totalOrders,
        totalRevenue,
        averageOrdersPerUser: totalUsers > 0 ? totalOrders / totalUsers : 0,
        averageRevenuePerUser: totalUsers > 0 ? totalRevenue / totalUsers : 0,
        topPerformers: userPerformances.slice(0, 5),
        bottomPerformers: userPerformances.slice(-5).reverse(),
        periodComparison: {
          orderGrowth: 0, // Would need previous period comparison
          revenueGrowth: 0
        }
      };
    } catch (error: any) {
      console.error('Error getting team performance:', error);
      throw new Error(`Failed to get team performance: ${error.message}`);
    }
  }

  /**
   * Compare user performance with team average
   * 
   * @param userId - User ID
   * @param period - Performance period
   * @returns Promise resolving to performance comparison
   */
  static async compareUserPerformance(
    userId: string,
    period: PerformancePeriod = 'monthly'
  ): Promise<PerformanceComparison> {
    try {
      const userPerf = await this.getUserPerformance(userId, period);
      const teamPerf = await this.getTeamPerformance(period);

      const allPerformances = [
        ...teamPerf.topPerformers,
        ...teamPerf.bottomPerformers
      ].sort((a, b) => b.totalOrders - a.totalOrders);

      const userRank = allPerformances.findIndex(p => p.userId === userId) + 1;
      const percentile = ((allPerformances.length - userRank + 1) / allPerformances.length) * 100;

      return {
        user: userPerf,
        average: {
          totalOrders: teamPerf.averageOrdersPerUser,
          totalRevenue: teamPerf.averageRevenuePerUser,
          storesVisited: 0
        },
        percentile
      };
    } catch (error: any) {
      console.error('Error comparing performance:', error);
      throw new Error(`Failed to compare performance: ${error.message}`);
    }
  }

  /**
   * Get period dates
   * 
   * @private
   */
  private static getPeriodDates(
    period: PerformancePeriod,
    customStart?: Date,
    customEnd?: Date
  ): { start: Date; end: Date } {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (period) {
      case 'daily':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'weekly':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'yearly':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        start = customStart || new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = customEnd || now;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    return { start, end };
  }
}

